import { useEffect, useRef } from 'react'
import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import { Bold, Italic, List, ListOrdered, Link as LinkIcon, Quote, Strikethrough, Heading2 } from 'lucide-react'
import { cn } from '@/lib/utils'

/** TipTap zwraca <p></p> dla pustego dokumentu - normalizujemy do pustego stringa. */
function normalizeHtml(html: string): string {
  return html === '<p></p>' ? '' : html
}

type Props = {
  value: string
  onChange: (html: string) => void
  disabled?: boolean
  placeholder?: string
  className?: string
}

/**
 * Minimal TipTap-based rich text editor stylowany w stylu shadcn/Tailwind.
 * Pasek narzędzi: bold, italic, strike, H2, bullet, ordered, blockquote, link.
 * Zwraca HTML przez {@code onChange} (TipTap sanityzuje na poziomie ProseMirror).
 */
export function RichTextEditor({ value, onChange, disabled, placeholder, className }: Props) {
  // Ostatnia wartość, którą wystawiliśmy lub odebraliśmy z parenta. Blur emituje
  // onChange tylko gdy aktualny HTML się od niej różni - "kliknięcie + blur bez
  // zmian" nie wywoła zbędnego PATCH-a.
  const lastValueRef = useRef<string>(value || '')

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-sky-700 underline underline-offset-2' },
      }),
    ],
    content: value || '',
    editable: !disabled,
    onBlur: ({ editor }) => {
      const html = normalizeHtml(editor.getHTML())
      if (html === normalizeHtml(lastValueRef.current)) return
      lastValueRef.current = html
      onChange(html)
    },
  })

  // Resync gdy parent zmieni value (np. server reload). Nie nadpisuj gdy edytor
  // jest focused - przerywałoby to pisanie i resetowało kursor.
  useEffect(() => {
    if (!editor) return
    lastValueRef.current = value || ''
    if (editor.isFocused) return
    const current = normalizeHtml(editor.getHTML())
    const incoming = value || ''
    if (current === incoming) return
    editor.commands.setContent(incoming, { emitUpdate: false })
  }, [value, editor])

  useEffect(() => {
    editor?.setEditable(!disabled)
  }, [disabled, editor])

  if (!editor) return null

  return (
    <div className={cn('rounded border bg-white', disabled && 'opacity-60 bg-slate-50', className)}>
      <Toolbar editor={editor} disabled={disabled} />
      <div
        className={cn(
          'px-2 py-1.5 min-h-[6rem] text-sm focus-within:outline-none',
          // Minimalny styl treści (zastępuje @tailwindcss/typography).
          '[&_p]:my-1 [&_strong]:font-semibold [&_em]:italic',
          '[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-2 [&_h2]:mb-1',
          '[&_h3]:font-medium [&_h3]:mt-2 [&_h3]:mb-1',
          '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1',
          '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1',
          '[&_blockquote]:border-l-2 [&_blockquote]:border-slate-300 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:my-1',
          '[&_a]:text-sky-700 [&_a]:underline [&_a]:underline-offset-2',
          '[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[5rem]',
        )}
      >
        <EditorContent editor={editor} placeholder={placeholder} />
      </div>
    </div>
  )
}

function Toolbar({ editor, disabled }: { editor: Editor; disabled?: boolean }) {
  const btn = (active: boolean) =>
    cn(
      'p-1 rounded text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed',
      active && 'bg-slate-200 text-slate-900',
    )
  const promptLink = () => {
    const prev = editor.getAttributes('link').href ?? ''
    const url = window.prompt('Adres URL (puste = usuń link):', prev)
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }
  return (
    <div className="flex items-center gap-0.5 px-1.5 py-1 border-b border-slate-200">
      <button
        type="button"
        tabIndex={-1}
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={disabled}
        className={btn(editor.isActive('bold'))}
        aria-label="Pogrubienie"
        title="Pogrubienie (Ctrl+B)"
      >
        <Bold size={14} />
      </button>
      <button
        type="button"
        tabIndex={-1}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={disabled}
        className={btn(editor.isActive('italic'))}
        aria-label="Kursywa"
        title="Kursywa (Ctrl+I)"
      >
        <Italic size={14} />
      </button>
      <button
        type="button"
        tabIndex={-1}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={disabled}
        className={btn(editor.isActive('strike'))}
        aria-label="Przekreślenie"
        title="Przekreślenie"
      >
        <Strikethrough size={14} />
      </button>
      <span className="w-px h-4 bg-slate-200 mx-1" />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        disabled={disabled}
        className={btn(editor.isActive('heading', { level: 2 }))}
        aria-label="Nagłówek"
        title="Nagłówek"
      >
        <Heading2 size={14} />
      </button>
      <button
        type="button"
        tabIndex={-1}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        disabled={disabled}
        className={btn(editor.isActive('bulletList'))}
        aria-label="Lista wypunktowana"
        title="Lista wypunktowana"
      >
        <List size={14} />
      </button>
      <button
        type="button"
        tabIndex={-1}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        disabled={disabled}
        className={btn(editor.isActive('orderedList'))}
        aria-label="Lista numerowana"
        title="Lista numerowana"
      >
        <ListOrdered size={14} />
      </button>
      <button
        type="button"
        tabIndex={-1}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        disabled={disabled}
        className={btn(editor.isActive('blockquote'))}
        aria-label="Cytat"
        title="Cytat"
      >
        <Quote size={14} />
      </button>
      <span className="w-px h-4 bg-slate-200 mx-1" />
      <button
        type="button"
        tabIndex={-1}
        onClick={promptLink}
        disabled={disabled}
        className={btn(editor.isActive('link'))}
        aria-label="Link"
        title="Wstaw / usuń link"
      >
        <LinkIcon size={14} />
      </button>
    </div>
  )
}
